'use client'

import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import type { PlanChanges } from '@/lib/billing/plan-changes'
import type { PlanConflictError } from '@/lib/billing/plans/plan-write'
import { describePlanChanges, describePlanValues, formatPlanTimestamp } from './change-format'
import { ChangeList } from './change-list'
import { PlanDetails } from './plan-details'
import type { PlanPayload } from './types'
import { useTranslations } from "next-intl"
import { usePlanPresentation } from "./use-plan-presentation"




export interface PlanReview {
  /** Exactly what "Confirm" sends. */
  payload: PlanPayload
  /** `null` for a new plan. */
  changes: PlanChanges | null
}

interface ReviewDialogProps {
  review: PlanReview | null
  saving: boolean
  error: string | null
  /** Set on a 409; only a reload can continue. */
  conflict: PlanConflictError | null
  onConfirm: () => void
  onBack: () => void
  onReload: () => void
}

export function ReviewDialog({ review, saving, error, conflict, onConfirm, onBack, onReload }: ReviewDialogProps) {
  const presentation = usePlanPresentation()

  const tUi = useTranslations("Billing.BillingPlanEditorReviewDialog")

  const changes = review?.changes ?? null
  function keepOpenWhileSaving(event: Event) {
    if (saving) event.preventDefault()
  }
  return (
    <Dialog open={review !== null} onOpenChange={open => { if (!open && !saving) onBack() }}>
      <DialogContent className="sm:max-w-2xl" onEscapeKeyDown={keepOpenWhileSaving} onInteractOutside={keepOpenWhileSaving}>
        {review ? <>
          <DialogHeader>
            <DialogTitle><span key={changes ? tUi("reviewChanges") : tUi("reviewNewPlan")}>{changes ? tUi("reviewChanges") : tUi("reviewNewPlan")}</span></DialogTitle>
            <DialogDescription>
              <span key={changes ? tUi("onlyTheseSettingsChangeNothingIsSavedUntil") : tUi("checkEverySettingBeforeCreatingThePlan")}>{changes ? tUi("onlyTheseSettingsChangeNothingIsSavedUntil") : tUi("checkEverySettingBeforeCreatingThePlan")}</span>
            </DialogDescription>
          </DialogHeader>
          {changes ? <>
            <ChangeList rows={describePlanChanges(changes, presentation)} />
            <details className="rounded-md border p-3">
              <summary className="cursor-pointer text-sm font-semibold"><span key={tUi("fullPlan")}>{tUi("fullPlan")}</span></summary>
              <div className="mt-3"><PlanDetails rows={describePlanValues(review.payload, presentation)} /></div>
            </details>
          </> : <PlanDetails rows={describePlanValues(review.payload, presentation)} />}
          {error ? <p role="alert" className="rounded-md border border-destructive p-3 text-sm text-destructive">{error}</p> : null}
          {conflict ? <ConflictNotice conflict={conflict} /> : null}
          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" disabled={saving} onClick={onBack}><span key={tUi("backToEditing")}>{tUi("backToEditing")}</span></Button>
            {conflict
              ? <Button type="button" variant="destructive" onClick={onReload}><span key={tUi("discardMyChangesAndReload")}>{tUi("discardMyChangesAndReload")}</span></Button>
              : <Button type="button" disabled={saving} onClick={onConfirm}>
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {/* Own element: a translated label beside the spinner crashes React. */}
                <span key={changes ? tUi("confirmAndSave") : tUi("confirmAndCreate")}>{changes ? tUi("confirmAndSave") : tUi("confirmAndCreate")}</span>
              </Button>}
          </DialogFooter>
        </> : null}
      </DialogContent>
    </Dialog>
  )
}

function ConflictNotice({ conflict }: { conflict: PlanConflictError }) {
  const presentation = usePlanPresentation()


  const tUi = useTranslations("Billing.BillingPlanEditorReviewDialog")

  const name = conflict.changedBy ?? tUi('anotherProcess')
  const message = conflict.changedAt
    ? tUi('changedByAt', { name, when: formatPlanTimestamp(conflict.changedAt, presentation) })
    : tUi('changedBy', { name })
  return (
    <div role="alert" className="space-y-1 rounded-md border border-destructive p-3 text-sm">
      <p key={message} className="font-medium">{message}</p>
      <p className="text-muted-foreground"><span key={tUi("yourDraftIsKeptToSaveReloadThe")}>{tUi("yourDraftIsKeptToSaveReloadThe")}</span></p>
    </div>
  )
}
